import { useOnChange } from "@prismicio/editor-support/React"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import type { ErrorBoundary } from "@prismicio/editor-ui"
import {
  FilledDocumentLinkContent,
  FilledExternalLinkContent,
  FilledFileLinkContent,
  FilledImageLinkContent,
  type LinkContent,
} from "@prismicio/types-internal/lib/content"
import type { Link } from "@prismicio/types-internal/lib/customtypes"
import { useRef, useState } from "react"

import { defaultImageDimension } from "../../constants/images"
import type { MediaAssetOrExternalImage } from "../../core/MediaLibrary/hooks/mediaLibraryData"
import { getDocumentById } from "../../core/service"
import { throwConfigError, useEditorConfig } from "../../EditorConfig"
import { useFieldContext } from "../../FieldContext"
import { getFieldPlaceholder } from "../utils"
import type { FieldSelectType } from "./LinkFieldActions"

interface useLinkFieldParams {
  field: Link
  content: LinkContent | undefined
  onContentChange: (content: LinkContent | undefined) => void
}

export function useLinkField(params: useLinkFieldParams) {
  const { field, content, onContentChange } = params

  const fieldValue = getFieldValue()

  const displayTextEnabled = field.config?.allowText ?? false
  const displayText = (displayTextEnabled && content?.value.text) || undefined

  const variants = field.config?.variants ?? []
  const isValidVariant =
    content?.value.variant && variants.includes(content?.value.variant)
  const variant = isValidVariant ? content?.value.variant : variants[0]

  const [contentType, setContentType] = useState(getContentType)

  const linkTarget =
    (FilledExternalLinkContent.is(content?.value) && content?.value.target) ||
    undefined

  const [hasError, setHasError] = useState(false)
  const errorBoundaryRef = useRef<ErrorBoundary>(null)

  useOnChange(fieldValue, onFieldValueChange)

  const selectType = contentType
    ? contentTypeToSelectMap[contentType]
    : undefined

  function getContentType() {
    if (!field.config?.select || content?.value.__TYPE__)
      return content?.value.__TYPE__

    return selectToContentTypeMap[field.config?.select]
  }

  function getFieldValue() {
    if (!content) return ""

    if (FilledDocumentLinkContent.is(content.value)) return content.value.id
    if (FilledExternalLinkContent.is(content.value)) return content.value.url
    if (
      FilledFileLinkContent.is(content.value) ||
      FilledImageLinkContent.is(content.value)
    )
      return content.value.name

    return ""
  }

  type UpdateContentWithNoLinkArgs = {
    displayText?: string
    variant?: string
  }

  function updateContentWithNoLink(args: UpdateContentWithNoLinkArgs) {
    const { displayText, variant } = args

    if (!displayText && !variant) return onContentChange(undefined)

    switch (field.config?.select) {
      case "web":
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "ExternalLink",
            kind: "web",
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      case "document":
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "DocumentLink",
            kind: "document",
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      case "media":
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "MediaLink",
            kind: "media",
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      default:
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "AnyLink",
            kind: "any",
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
    }
  }

  function updateLinkContent(value: string, options?: { newTab: boolean }) {
    if (!value) return updateContentWithNoLink({ displayText, variant })

    const { newTab = false } = options ?? {}
    const newTarget = newTab ? "_blank" : undefined

    switch (contentType) {
      case "ExternalLink":
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "ExternalLink",
            url: value,
            target: newTarget,
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      case "DocumentLink":
        return onContentChange({
          __TYPE__: "LinkContent",
          key: getContentKey(content),
          value: {
            __TYPE__: "DocumentLink",
            id: value,
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      default:
        return onContentChange(undefined)
    }
  }

  function updateDisplayTextContent(displayText: string) {
    if (content) {
      return onContentChange({
        __TYPE__: content.__TYPE__,
        key: content.key,
        value: { ...content.value, text: displayText },
      })
    }

    return updateContentWithNoLink({ displayText })
  }

  function updateVariantContent(newVariant: string) {
    if (content) {
      return onContentChange({
        __TYPE__: content.__TYPE__,
        key: content.key,
        value: { ...content.value, variant: newVariant },
      })
    }

    return updateContentWithNoLink({
      variant: newVariant,
    })
  }

  const trackLinkAddedEvent = useTrackLinkAddedEvent()

  function onTypeChange(newValue?: FieldSelectType) {
    setContentType(newValue ? selectToContentTypeMap[newValue] : undefined)
    updateLinkContent("")

    if (newValue && newValue !== "any") trackLinkAddedEvent(newValue)
  }

  function onDocumentSelected(documentId: string) {
    updateLinkContent(documentId)
  }

  function onError() {
    setHasError(true)
  }

  function onFieldValueChange() {
    if (!hasError) return
    setHasError(false)
    errorBoundaryRef.current?.reset()
  }

  function onMediaSelected(newMedia: MediaAssetOrExternalImage) {
    const mappedMedia = mapMediaToLinkContent({ media: newMedia })
    switch (mappedMedia.value.__TYPE__) {
      case "ImageLink":
        return onContentChange({
          __TYPE__: mappedMedia.__TYPE__,
          key: mappedMedia.key,
          value: {
            ...mappedMedia.value,
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
      case "FileLink":
        return onContentChange({
          __TYPE__: mappedMedia.__TYPE__,
          key: mappedMedia.key,
          value: {
            ...mappedMedia.value,
            ...(displayText && { text: displayText }),
            ...(variant && { variant }),
          },
        })
    }
  }

  return {
    contentType,
    selectType,
    fieldValue,
    onDisplayTextChange: updateDisplayTextContent,
    onValueChange: updateLinkContent,
    onTypeChange,
    placeholder: getFieldPlaceholder(field),
    isTypeSelectEnabled: !field.config?.select,
    onDocumentSelected,
    onError,
    errorBoundaryRef,
    isTargetNewTab: linkTarget === "_blank",
    onMediaSelected,
    displayTextEnabled,
    displayText: displayText ?? "",
    variants,
    variant,
    onVariantChange: updateVariantContent,
  }
}

function getContentKey(content: LinkContent | undefined) {
  if (content) return content.key
  return crypto.randomUUID()
}

const contentTypeToSelectMap = {
  AnyLink: "any",
  DocumentLink: "document",
  ExternalLink: "web",
  FileLink: "media",
  ImageLink: "media",
  MediaLink: "media",
} as const

const selectToContentTypeMap = {
  any: "AnyLink",
  document: "DocumentLink",
  web: "ExternalLink",
  media: "FileLink",
} as const

export function useDocumentTitle(id: string) {
  const config = useEditorConfig()

  if (!config.searchDocuments) throwConfigError("searchDocuments")

  const { coreApiBaseUrl, authStrategy } = config

  const response = useRequest(getDocumentById, [
    coreApiBaseUrl,
    authStrategy,
    id,
  ])

  return response.title
}

// Translating a document ID to its title is nice but quite low priority.
// However, some documents have hundreds of content relationships in a single Group
// so if they scroll fast, we can't have them wait for minutes to see the data.
// There's already a limiting factor when it comes to the number of LinkField requests we do in parallel:
// The GroupItems are lazily rendered.
cache.registerFetcherConfig(getDocumentById, { concurrency: 50 })

interface MapMediaToLinkContentArgs {
  media: MediaAssetOrExternalImage
}

export function mapMediaToLinkContent(args: MapMediaToLinkContentArgs) {
  const { media } = args
  const isMediaLibraryItem = "kind" in media
  const isImage = isMediaLibraryItem && media.kind === "image"

  if (isImage) {
    return {
      __TYPE__: "LinkContent",
      key: crypto.randomUUID(),
      value: {
        __TYPE__: "ImageLink",
        id: media.id,
        name: media.filename,
        url: media.url,
        kind: "image",
        size: media.size.toString(),
        height: media.height?.toString() ?? `${defaultImageDimension}`,
        width: media.width?.toString() ?? `${defaultImageDimension}`,
        date: media.last_modified?.toString() ?? "",
      },
    } satisfies LinkContent
  } else if (isMediaLibraryItem) {
    return {
      __TYPE__: "LinkContent",
      key: crypto.randomUUID(),
      value: {
        __TYPE__: "FileLink",
        id: media.id,
        name: media.filename,
        kind: "file",
        url: media.url,
        size: media.size.toString(),
        date: media.last_modified?.toString() ?? "",
      },
    } satisfies LinkContent
  } else {
    // External image
    return {
      __TYPE__: "LinkContent",
      key: crypto.randomUUID(),
      value: {
        __TYPE__: "ImageLink",
        id: media.id,
        name: media?.alt ?? media.url,
        kind: "image",
        url: media.url,
        size: "0",
        height: media.height?.toString() ?? `${defaultImageDimension}`,
        width: media.width?.toString() ?? `${defaultImageDimension}`,
      },
    } satisfies LinkContent
  }
}

const useTrackLinkAddedEvent = () => {
  const fieldContext = useFieldContext()
  const { onAnalyticsEvent } = useEditorConfig()

  function trackLinkAddedEvent(type: "web" | "media" | "document") {
    if (!fieldContext) return
    if (!onAnalyticsEvent) return

    onAnalyticsEvent("Generic Link Added", {
      type,
      isInSlice: String(fieldContext.isInSlice),
      isInGroup: String(fieldContext.isInGroup),
      isInRepeatable: String(fieldContext.isInRepeatable),
      customTypeFormat: fieldContext.customTypeFormat ?? "custom",
    })
  }

  return trackLinkAddedEvent
}
